import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import type { TradeAccountDto } from '@ecom/contracts';
import { useTradeAccountsStore } from '@/stores/trade-accounts.store';
import { useT } from '@/i18n/use-t';
import type { MessageKey } from '@/i18n/messages';
import { priceListOptions } from '@/lib/options';
import { AsyncSelect } from '@/components/async-select';
import { ToggleField } from '@/components/fields';
import { Section } from '@/components/page';
import { Alert, Button, Field, Input, Pill } from '@/components/ui';

/**
 * A customer's trade account (ADR-045): whether they buy for a business, at
 * which price list — or, suspended or listless, at retail like everyone else.
 */
export function TradeAccountPanel({ customerId }: { customerId: string }) {
  const { account, application, loading, error, load } = useTradeAccountsStore();
  const t = useT();
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    void load(customerId);
  }, [customerId, load]);

  return (
    <Section
      title={t('tradeAccount')}
      subtitle={t('tradeAccountHint')}
      action={
        !loading && !editing ? (
          <Button variant="secondary" size="sm" onClick={() => setEditing(true)}>
            {account ? t('editEntry') : t('makeTradeAccount')}
          </Button>
        ) : undefined
      }
      flush
    >
      <div className="border-t border-line px-5 py-4 text-sm">
        {error && !editing ? <Alert>{error}</Alert> : null}
        {editing ? (
          <TradeAccountForm customerId={customerId} account={account} onDone={() => setEditing(false)} />
        ) : account ? (
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5">
            <dt className="text-ink-muted">{t('tradeStatus')}</dt>
            <dd>
              <Pill tone={account.tradePricing ? 'success' : 'neutral'}>
                {account.tradePricing ? t('tradePricingOn') : account.suspended ? t('tradeSuspended') : t('tradeRetail')}
              </Pill>
            </dd>
            <dt className="text-ink-muted">{t('legalName')}</dt>
            <dd className="text-ink">{account.legalName}</dd>
            {account.tradeName ? (
              <>
                <dt className="text-ink-muted">{t('tradeName')}</dt>
                <dd className="text-ink">{account.tradeName}</dd>
              </>
            ) : null}
            <dt className="text-ink-muted">{t('crNumber')}</dt>
            <dd data-numeric dir="ltr" className="text-ink">{account.crNumber}</dd>
            <dt className="text-ink-muted">{t('vatNumber')}</dt>
            <dd data-numeric dir="ltr" className="text-ink">{account.vatNumber ?? '—'}</dd>
            <dt className="text-ink-muted">{t('priceList')}</dt>
            <dd className="text-ink">{account.priceListName ?? '—'}</dd>
          </dl>
        ) : (
          <p className="text-ink-muted">{loading ? t('loading') : t('notTradeAccount')}</p>
        )}
        {application && !editing ? (
          // What they asked for, and what became of it (ADR-046).
          <p className="mt-3 border-t border-line pt-3 text-xs text-ink-muted">
            {t('latestApplication')}: <span dir="ltr">{application.number}</span> ·{' '}
            {t(`tradeStatus${application.status}` as MessageKey)} ·{' '}
            <Link to={`/trade-applications?open=${application.id}`} className="text-brand-700 hover:underline">
              {t('openApplication')}
            </Link>
          </p>
        ) : null}
      </div>
    </Section>
  );
}

function TradeAccountForm({
  customerId,
  account,
  onDone,
}: {
  customerId: string;
  account: TradeAccountDto | null;
  onDone: () => void;
}) {
  const { save, saving, error } = useTradeAccountsStore();
  const t = useT();
  const [legalName, setLegalName] = useState(account?.legalName ?? '');
  const [tradeName, setTradeName] = useState(account?.tradeName ?? '');
  const [crNumber, setCrNumber] = useState(account?.crNumber ?? '');
  const [vatNumber, setVatNumber] = useState(account?.vatNumber ?? '');
  const [line1, setLine1] = useState(account?.line1 ?? '');
  const [city, setCity] = useState(account?.city ?? '');
  const [priceListId, setPriceListId] = useState(account?.priceListId ?? '');
  const [suspended, setSuspended] = useState(account?.suspended ?? false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    const ok = await save(customerId, {
      legalName,
      tradeName: tradeName || null,
      crNumber,
      vatNumber: vatNumber || null,
      line1: line1 || null,
      city: city || null,
      priceListId: priceListId || null,
      suspended,
    });
    if (ok) onDone();
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-3">
      <Field label={t('legalName')} htmlFor="trade-legal">
        <Input id="trade-legal" value={legalName} onChange={(event) => setLegalName(event.target.value)} required maxLength={160} />
      </Field>
      <Field label={t('tradeName')} htmlFor="trade-name">
        <Input id="trade-name" value={tradeName} onChange={(event) => setTradeName(event.target.value)} maxLength={160} />
      </Field>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label={t('crNumber')} htmlFor="trade-cr" hint={t('crNumberHint')}>
          <Input id="trade-cr" dir="ltr" inputMode="numeric" value={crNumber} onChange={(event) => setCrNumber(event.target.value)} required />
        </Field>
        <Field label={t('vatNumber')} htmlFor="trade-vat" hint={t('vatNumberHint')}>
          <Input id="trade-vat" dir="ltr" inputMode="numeric" value={vatNumber} onChange={(event) => setVatNumber(event.target.value)} />
        </Field>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label={t('tradeAddress')} htmlFor="trade-line1">
          <Input id="trade-line1" value={line1} onChange={(event) => setLine1(event.target.value)} maxLength={200} />
        </Field>
        <Field label={t('city')} htmlFor="trade-city">
          <Input id="trade-city" value={city} onChange={(event) => setCity(event.target.value)} maxLength={80} />
        </Field>
      </div>
      <Field label={t('priceList')} htmlFor="trade-list" hint={t('priceListChoiceHint')}>
        <AsyncSelect
          id="trade-list"
          value={priceListId}
          onChange={(id) => setPriceListId(id)}
          fetchPage={priceListOptions.page}
          fetchOne={priceListOptions.one}
          emptyLabel={t('tradeRetail')}
        />
      </Field>
      <ToggleField label={t('tradeSuspended')} hint={t('tradeSuspendedHint')} checked={suspended} onChange={setSuspended} />

      {error ? <Alert>{error}</Alert> : null}

      <div className="flex justify-end gap-2">
        <Button type="button" variant="secondary" onClick={onDone}>{t('cancel')}</Button>
        <Button type="submit" disabled={saving}>{saving ? t('saving') : t('save')}</Button>
      </div>
    </form>
  );
}
